import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import { useAuth } from '../../context/AuthContext';
import {
  FOLLOW_UP_LABELS,
  FollowUpFilter,
  PARTNER_CATEGORIES,
  PARTNER_CATEGORY_LABELS,
  PARTNER_TIERS,
  PARTNER_TIER_LABELS,
  PROSPECT_STATUSES,
  PROSPECT_STATUS_LABELS,
  PROSPECT_STATUS_TONES,
} from '../../constants/partnerNetwork';
import {
  Prospect,
  ProspectFilters,
  ProspectStats,
  getProspectStats,
  listProspects,
  logProspectActivity,
} from '../../services/partnerNetwork';
import { formatDate, isOverdue, relativeDays } from '../../utils/partnerFormat';
import { defaultOutreachMessage, whatsappUrl } from '../../utils/whatsapp';
import BulkEmailModal from './BulkEmailModal';
import ProspectForm from './ProspectForm';
import {
  Alert,
  EmptyState,
  Pagination,
  Spinner,
  StatCard,
  StatusBadge,
  filterClass,
} from './ui';

type SortField = 'createdAt' | 'fullName' | 'companyName' | 'status' | 'nextFollowUpAt';

const SORT_COLUMNS: Array<{ field: SortField; label: string }> = [
  { field: 'fullName', label: 'Name' },
  { field: 'companyName', label: 'Company' },
  { field: 'status', label: 'Status' },
  { field: 'nextFollowUpAt', label: 'Next follow-up' },
];

const PAGE_SIZE = 25;

/**
 * The marketer's daily board: follow-up counts, a filterable table, and the
 * three actions that make up the job — email, WhatsApp and moving the status.
 */
const ProspectList: React.FC = () => {
  const { admin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [stats, setStats] = useState<ProspectStats | null>(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: PAGE_SIZE, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Prospect | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  /* --- filters live in the URL, so a filtered board can be linked to --- */

  const followUp = (searchParams.get('followUp') || '') as FollowUpFilter | '';
  const status = searchParams.get('status') || '';
  const category = searchParams.get('category') || '';
  const tier = searchParams.get('tier') || '';
  const mine = searchParams.get('mine') === 'true';
  const sort = (searchParams.get('sort') || 'createdAt') as SortField;
  const direction = (searchParams.get('direction') || 'desc') as 'asc' | 'desc';
  const page = Number(searchParams.get('page') || 1);

  // The search box is local and debounced into the URL, so typing does not
  // push a history entry per keystroke.
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const search = searchParams.get('search') || '';

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput === search) return;
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (searchInput) next.set('search', searchInput);
        else next.delete('search');
        next.delete('page');
        return next;
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput, search, setSearchParams]);

  const setFilter = useCallback(
    (key: string, value: string | null) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        // Any filter change invalidates the page you were on.
        if (key !== 'page') next.delete('page');
        return next;
      });
    },
    [setSearchParams]
  );

  const filters: ProspectFilters = useMemo(
    () => ({
      search: search || undefined,
      status: status || undefined,
      category: category || undefined,
      tier: tier || undefined,
      followUp: followUp || undefined,
      assignedMarketerId: mine ? 'me' : undefined,
      sort,
      direction,
      page,
      limit: PAGE_SIZE,
    }),
    [search, status, category, tier, followUp, mine, sort, direction, page]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listProspects(filters);
      setProspects(response.data);
      setPagination(response.pagination);
      // Selections refer to rows that may no longer be listed.
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load prospects');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadStats = useCallback(async () => {
    try {
      setStats(await getProspectStats(mine));
    } catch {
      // The counts are a convenience; the table below still works without them.
    }
  }, [mine]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  /* --- selection --- */

  const toggleRow = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = prospects.length > 0 && selected.size === prospects.length;

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(prospects.map((p) => p.id)));
  };

  const selectedProspects = useMemo(
    () => prospects.filter((prospect) => selected.has(prospect.id)),
    [prospects, selected]
  );

  /* --- row actions --- */

  const openWhatsApp = async (prospect: Prospect) => {
    const url = whatsappUrl(
      prospect.phone,
      defaultOutreachMessage({
        name: prospect.fullName,
        companyName: prospect.companyName,
        senderName: admin?.name,
      })
    );

    if (!url) {
      setError(`${prospect.fullName} has no usable phone number.`);
      return;
    }

    // Opened first so the click stays inside the user gesture — waiting on the
    // network here would get the new tab blocked as a popup.
    window.open(url, '_blank', 'noopener,noreferrer');

    try {
      await logProspectActivity(prospect.id, {
        type: 'whatsapp_opened',
        description: `WhatsApp opened — ${prospect.phone}`,
        markContacted: true,
      });
      loadStats();
    } catch {
      // The conversation is already open; failing to log it is not worth an
      // error in the marketer's face.
    }
  };

  const emailOne = (prospect: Prospect) => {
    setSelected(new Set([prospect.id]));
    setEmailOpen(true);
  };

  const sortBy = (field: SortField) => {
    if (sort === field) {
      setFilter('direction', direction === 'asc' ? 'desc' : 'asc');
    } else {
      setFilter('sort', field);
      setFilter('direction', 'asc');
    }
  };

  const hasFilters = Boolean(
    search || status || category || tier || followUp || mine
  );

  const clearFilters = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  };

  /* --- render --- */

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="mb-2 font-display text-2xl font-semibold text-white md:text-3xl">
                Partner prospects
              </h1>
              <p className="text-sm text-muted">
                Everyone in the partner acquisition pipeline, from first contact to onboarding.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              className="btn-primary py-2.5 px-5"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add prospect
            </button>
          </div>
        </AnimatedSection>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}
        {notice && !error && (
          <Alert tone="success" className="mb-6">
            {notice}
          </Alert>
        )}

        {/* Follow-up board */}
        <AnimatedSection animation="fade-in">
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatCard
              label="Today"
              value={stats?.followUps.today ?? '—'}
              hint="Follow up today"
              tone={stats?.followUps.today ? 'warning' : 'default'}
              active={followUp === 'today'}
              onClick={() => setFilter('followUp', followUp === 'today' ? null : 'today')}
            />
            <StatCard
              label="Next 3 days"
              value={stats?.followUps.next3 ?? '—'}
              hint="Today through day three"
              active={followUp === 'next3'}
              onClick={() => setFilter('followUp', followUp === 'next3' ? null : 'next3')}
            />
            <StatCard
              label="Overdue"
              value={stats?.followUps.overdue ?? '—'}
              hint="Past their follow-up date"
              tone={stats?.followUps.overdue ? 'danger' : 'default'}
              active={followUp === 'overdue'}
              onClick={() => setFilter('followUp', followUp === 'overdue' ? null : 'overdue')}
            />
            <StatCard
              label="No follow-up"
              value={stats?.followUps.unscheduled ?? '—'}
              hint="Nothing scheduled"
              active={followUp === 'none'}
              onClick={() => setFilter('followUp', followUp === 'none' ? null : 'none')}
            />
            <StatCard
              label="Total prospects"
              value={stats?.total ?? '—'}
              hint={mine ? 'Assigned to you' : 'Across the pipeline'}
            />
          </div>
        </AnimatedSection>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-surface-border bg-surface-card p-4">
          <div className="relative min-w-[200px] flex-1">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, company, email, phone…"
              className={`${filterClass} w-full pl-9`}
            />
          </div>

          <select
            value={status}
            onChange={(e) => setFilter('status', e.target.value || null)}
            className={filterClass}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {PROSPECT_STATUSES.map((value) => (
              <option key={value} value={value}>
                {PROSPECT_STATUS_LABELS[value]}
              </option>
            ))}
          </select>

          <select
            value={category}
            onChange={(e) => setFilter('category', e.target.value || null)}
            className={filterClass}
            aria-label="Filter by category"
          >
            <option value="">All categories</option>
            {PARTNER_CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {PARTNER_CATEGORY_LABELS[value]}
              </option>
            ))}
          </select>

          <select
            value={tier}
            onChange={(e) => setFilter('tier', e.target.value || null)}
            className={filterClass}
            aria-label="Filter by tier"
          >
            <option value="">All tiers</option>
            {PARTNER_TIERS.map((value) => (
              <option key={value} value={value}>
                {PARTNER_TIER_LABELS[value]}
              </option>
            ))}
          </select>

          <select
            value={followUp}
            onChange={(e) => setFilter('followUp', e.target.value || null)}
            className={filterClass}
            aria-label="Filter by follow-up"
          >
            <option value="">Any follow-up</option>
            {(['today', 'tomorrow', 'next3', 'overdue', 'none'] as FollowUpFilter[]).map(
              (value) => (
                <option key={value} value={value}>
                  {FOLLOW_UP_LABELS[value]}
                </option>
              )
            )}
          </select>

          <label className="flex items-center gap-2 text-sm text-muted">
            <input
              type="checkbox"
              checked={mine}
              onChange={(e) => setFilter('mine', e.target.checked ? 'true' : null)}
              className="h-4 w-4 accent-[#16A34A]"
            />
            Mine only
          </label>

          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-muted underline underline-offset-2 hover:text-white"
            >
              Clear
            </button>
          )}
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-primary-500/30 bg-primary-500/5 px-4 py-3">
            <p className="text-sm text-white">
              {selected.size} selected
            </p>
            <button
              type="button"
              onClick={() => setEmailOpen(true)}
              className="btn-primary py-2 px-4 text-sm"
            >
              Send email
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-sm text-muted underline underline-offset-2 hover:text-white"
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Table */}
        <div className="rounded-xl border border-surface-border bg-surface-card">
          {loading ? (
            <div className="flex justify-center py-20">
              <Spinner className="h-10 w-10" />
            </div>
          ) : prospects.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title={hasFilters ? 'No prospects match those filters' : 'No prospects yet'}
                description={
                  hasFilters
                    ? 'Try widening the search, or clear the filters to see the whole pipeline.'
                    : 'Add the first person you want to bring into the partner network.'
                }
                action={
                  hasFilters ? (
                    <button type="button" onClick={clearFilters} className="btn-secondary py-2.5 px-5">
                      Clear filters
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(null);
                        setFormOpen(true);
                      }}
                      className="btn-primary py-2.5 px-5"
                    >
                      Add prospect
                    </button>
                  )
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-muted">
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        aria-label="Select all on this page"
                        className="h-4 w-4 accent-[#16A34A]"
                      />
                    </th>
                    {SORT_COLUMNS.map((column) => (
                      <th key={column.field} className="px-4 py-3 font-medium">
                        <button
                          type="button"
                          onClick={() => sortBy(column.field)}
                          className="inline-flex items-center gap-1 hover:text-white"
                        >
                          {column.label}
                          {sort === column.field && (
                            <span aria-hidden>{direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                    ))}
                    <th className="px-4 py-3 font-medium">Category</th>
                    <th className="px-4 py-3 font-medium">Country</th>
                    <th className="px-4 py-3 font-medium">Last contacted</th>
                    <th className="px-4 py-3 font-medium">Contact</th>
                    <th className="px-4 py-3 font-medium">Marketer</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {prospects.map((prospect) => {
                    const overdue = isOverdue(prospect.nextFollowUpAt);

                    return (
                      <tr
                        key={prospect.id}
                        className="border-b border-surface-border/60 transition-colors last:border-0 hover:bg-surface-hover/40"
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selected.has(prospect.id)}
                            onChange={() => toggleRow(prospect.id)}
                            aria-label={`Select ${prospect.fullName}`}
                            className="h-4 w-4 accent-[#16A34A]"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/admin/partners/prospects/${prospect.id}`}
                            className="font-medium text-white hover:text-primary-400"
                          >
                            {prospect.fullName}
                          </Link>
                          {prospect.jobTitle && (
                            <span className="block text-xs text-muted">{prospect.jobTitle}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted">{prospect.companyName || '—'}</td>
                        <td className="px-4 py-3">
                          <StatusBadge
                            label={PROSPECT_STATUS_LABELS[prospect.status]}
                            tone={PROSPECT_STATUS_TONES[prospect.status]}
                          />
                        </td>
                        <td className="px-4 py-3">
                          {prospect.nextFollowUpAt ? (
                            <span className={overdue ? 'text-red-400' : 'text-white'}>
                              {formatDate(prospect.nextFollowUpAt)}
                              <span className="block text-xs text-muted">
                                {relativeDays(prospect.nextFollowUpAt)}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted">Not set</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted">
                          {prospect.category
                            ? PARTNER_CATEGORY_LABELS[prospect.category]
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-muted">{prospect.country || '—'}</td>
                        <td className="px-4 py-3 text-muted">
                          {formatDate(prospect.lastContactedAt)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="block max-w-[180px] truncate text-muted">
                            {prospect.email || '—'}
                          </span>
                          <span className="block text-xs text-muted/70">
                            {prospect.phone || ''}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted">
                          {prospect.assignedMarketerName || 'Unassigned'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Link
                              to={`/admin/partners/prospects/${prospect.id}`}
                              title="View"
                              className="rounded-lg p-2 text-muted transition-colors hover:bg-surface-hover hover:text-white"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </Link>
                            <button
                              type="button"
                              onClick={() => {
                                setEditing(prospect);
                                setFormOpen(true);
                              }}
                              title="Edit"
                              className="rounded-lg p-2 text-muted transition-colors hover:bg-surface-hover hover:text-white"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => emailOne(prospect)}
                              disabled={!prospect.email}
                              title={prospect.email ? 'Send email' : 'No email address'}
                              className="rounded-lg p-2 text-muted transition-colors hover:bg-surface-hover hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => openWhatsApp(prospect)}
                              disabled={!prospect.phone}
                              title={prospect.phone ? 'Open WhatsApp' : 'No phone number'}
                              className="rounded-lg p-2 text-muted transition-colors hover:bg-surface-hover hover:text-primary-400 disabled:cursor-not-allowed disabled:opacity-30"
                            >
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 016.988 2.898 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && prospects.length > 0 && (
            <div className="border-t border-surface-border px-4">
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                onChange={(next) => setFilter('page', String(next))}
              />
            </div>
          )}
        </div>
      </div>

      <ProspectForm
        open={formOpen}
        prospect={editing}
        onClose={() => setFormOpen(false)}
        onSaved={(prospect, created) => {
          setNotice(
            created
              ? `${prospect.fullName} added as ${prospect.prospectId}.`
              : `${prospect.fullName} updated.`
          );
          load();
          loadStats();
        }}
      />

      <BulkEmailModal
        open={emailOpen}
        prospects={selectedProspects}
        onClose={() => setEmailOpen(false)}
        onSent={() => {
          load();
          loadStats();
        }}
      />
    </section>
  );
};

export default ProspectList;
